import { useState, useEffect, useMemo, type InputHTMLAttributes } from 'react';
import {
  useService,
  useServices,
  useActiveServiceTypes,
  useCreateService,
  useUpdateService,
} from '../../services/httpServices/queries';
import { generateAcronymCode, makeUniqueCode } from '../../utils/code';
import { BILLING_UNITS, defaultUnitLabel } from '../../utils/servicePricing';
import {
  Loader2,
  AlertCircle,
  Plus,
  Save,
  X,
  Tags,
  Info,
  Wallet,
  Sparkles,
  type LucideIcon,
} from 'lucide-react';

/** Billing units whose price is charged per quantity rather than as a lump sum. */
type BillingUnit = 'flat' | 'per_unit' | 'per_hour' | 'per_day';

interface ServiceFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** When provided the modal edits that service; otherwise it creates a new one. */
  serviceId?: string;
}

const emptyForm = {
  name: '',
  code: '',
  description: '',
  serviceType: '',
  price: 0,
  taxPercent: 0,
  isActive: true,
  requiresDoctor: false,
  billingUnit: 'flat' as BillingUnit,
  unitLabel: '',
  minimumUnits: 0,
  unitIncrement: 0,
};

export function ServiceFormModal({ isOpen, onClose, serviceId }: ServiceFormModalProps) {
  const isEdit = !!serviceId;

  const { data: serviceData, isLoading: serviceLoading, error: serviceError } = useService(
    isOpen && serviceId ? serviceId : undefined,
  );
  const createMutation = useCreateService();
  const updateMutation = useUpdateService();

  // All existing service codes, used to keep an auto-generated code unique.
  // Only needed when creating (edit keeps the existing code untouched).
  const { data: allServicesData } = useServices(1, 1000, undefined, undefined, isOpen && !isEdit);

  // Dynamic, admin-managed service types (the Service Type dropdown options).
  const { data: serviceTypesData, isLoading: serviceTypesLoading } = useActiveServiceTypes();

  const [formData, setFormData] = useState({ ...emptyForm });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [codeManuallyEdited, setCodeManuallyEdited] = useState(false);

  const service = serviceData?.data;
  const serviceTypes = serviceTypesData?.data || [];
  const existingCodes = useMemo(
    () => (allServicesData?.data || []).map((item: any) => item.code).filter(Boolean) as string[],
    [allServicesData],
  );

  // Reset the form whenever the modal opens.
  useEffect(() => {
    if (!isOpen) return;
    setFormErrors({});
    if (!serviceId) {
      setFormData({ ...emptyForm });
      setCodeManuallyEdited(false);
    }
  }, [isOpen, serviceId]);

  // Auto-generate the code from the name (acronym), kept unique among existing
  // services and left editable — editing the code stops further auto-generation.
  useEffect(() => {
    if (isEdit) return;
    if (!formData.name.trim()) return;
    if (codeManuallyEdited) return;
    const base = generateAcronymCode(formData.name, 50);
    if (!base) return;
    const unique = makeUniqueCode(base, existingCodes, { maxLength: 50 });
    setFormData((prev) => (prev.code === unique ? prev : { ...prev, code: unique }));
  }, [formData.name, codeManuallyEdited, isEdit, existingCodes]);

  useEffect(() => {
    if (isEdit && service) {
      setFormData({
        name: service.name,
        code: service.code,
        description: service.description || '',
        serviceType: service.serviceType,
        price: service.price,
        taxPercent: service.taxPercent || 0,
        isActive: service.isActive,
        requiresDoctor: service.requiresDoctor,
        billingUnit: (service.billingUnit || 'flat') as BillingUnit,
        unitLabel: service.unitLabel || '',
        minimumUnits: service.minimumUnits || 0,
        unitIncrement: service.unitIncrement || 0,
      });
      setCodeManuallyEdited(true);
    }
  }, [isEdit, service]);

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    if (!formData.name.trim()) errors.name = 'Name is required';
    if (!formData.code.trim()) errors.code = 'Code is required';
    if (!formData.serviceType.trim()) errors.serviceType = 'Service type is required';
    if (formData.price < 0) errors.price = 'Price must be positive';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    const isFlat = formData.billingUnit === 'flat';
    const payload = {
      ...formData,
      price: Number(formData.price),
      taxPercent: Number(formData.taxPercent),
      unitLabel: isFlat ? '' : formData.unitLabel.trim(),
      minimumUnits: isFlat ? 0 : Number(formData.minimumUnits) || 0,
      unitIncrement:
        formData.billingUnit === 'per_hour' ? Number(formData.unitIncrement) || 0 : 0,
    };

    try {
      if (isEdit) {
        await updateMutation.mutateAsync({ id: serviceId!, data: payload });
      } else {
        await createMutation.mutateAsync(payload);
      }
      onClose();
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Something went wrong';
      setFormErrors({ general: message });
    }
  };

  const updateField = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (field === 'code') {
      setCodeManuallyEdited(String(value).trim() !== '');
    }
    if (formErrors[field]) {
      setFormErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const inputBase = 'w-full px-3 py-2 border rounded-lg bg-background text-text focus:outline-none focus:ring-2 text-sm transition-colors';
  const inputValid = 'border-border focus:ring-primary';
  const inputInvalid = 'border-danger focus:ring-danger';
  const labelClass = 'block text-xs font-medium text-text-muted mb-1';

  const ic = (field: string) => `${inputBase} ${formErrors[field] ? inputInvalid : inputValid}`;
  const err = (field: string) => formErrors[field] ? <p className="text-xs text-danger mt-1">{formErrors[field]}</p> : null;

  const isQuantityBilled = formData.billingUnit !== 'flat';
  const resolvedUnitLabel =
    formData.unitLabel.trim() || defaultUnitLabel(formData.billingUnit);
  const rateSuffix = isQuantityBilled && resolvedUnitLabel ? ` / ${resolvedUnitLabel}` : '';
  const isPending = createMutation.isPending || updateMutation.isPending;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-in fade-in duration-200">
      <div className="bg-surface rounded-2xl border border-border shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col animate-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-border bg-background/50 rounded-t-2xl shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <Tags size={20} className="text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-text">
                {isEdit ? 'Edit Service' : 'New Service'}
              </h2>
              <p className="text-xs text-text-muted">
                {isEdit ? 'Update service details and pricing' : 'Create a new billable service'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-background rounded-xl transition-colors"
            type="button"
          >
            <X size={18} className="text-text-muted" />
          </button>
        </div>

        {/* Body */}
        {isEdit && serviceLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : isEdit && (serviceError || !service) ? (
          <div className="flex items-center gap-2 p-4 bg-danger/10 border border-danger/20 rounded-lg text-danger m-5">
            <AlertCircle size={18} />
            <span>Failed to load service details. Please try again.</span>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col min-h-0 flex-1">
            <div className="overflow-y-auto flex-1">
              {formErrors.general && (
                <div className="m-5 mb-0 bg-danger/10 text-danger rounded-lg p-3 flex items-center gap-2 text-sm">
                  <AlertCircle size={16} />
                  <span className="font-medium">{formErrors.general}</span>
                </div>
              )}

              {/* Basic information */}
              <SectionHeader icon={Info} title="Basic Information" />
              <div className="p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className={labelClass}>Name *</label>
                  <input
                    type="text"
                    autoFocus
                    className={ic('name')}
                    value={formData.name}
                    onChange={(e) => updateField('name', e.target.value)}
                    placeholder="OPD Consultation"
                  />
                  {err('name')}
                </div>

                <div>
                  <label className={labelClass}>Code *</label>
                  <div className="relative">
                    <input
                      type="text"
                      className={`${ic('code')} font-mono pr-9`}
                      value={formData.code}
                      onChange={(e) => updateField('code', e.target.value)}
                      placeholder="CONS-OPD"
                    />
                    {!isEdit && (
                      <span
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted"
                        title="Auto-generated from the name. You can edit it."
                      >
                        <Sparkles size={15} />
                      </span>
                    )}
                  </div>
                  {err('code')}
                </div>

                <div>
                  <label className={labelClass}>Service Type *</label>
                  <select
                    className={ic('serviceType')}
                    value={formData.serviceType}
                    onChange={(e) => updateField('serviceType', e.target.value)}
                    disabled={serviceTypesLoading}
                  >
                    <option value="">{serviceTypesLoading ? 'Loading...' : 'Select Service Type'}</option>
                    {/* Keep the current value selectable when it is not in the active
                        list (e.g. an inactive or legacy type) so editing a service
                        never silently changes its type. */}
                    {formData.serviceType &&
                      !serviceTypes.some((type: any) => type.code === formData.serviceType) && (
                        <option value={formData.serviceType}>{formData.serviceType} (inactive)</option>
                      )}
                    {serviceTypes.map((type: any) => (
                      <option key={type.id} value={type.code}>
                        {type.name}
                      </option>
                    ))}
                  </select>
                  {err('serviceType')}
                </div>

                <div className="sm:col-span-2 lg:col-span-3">
                  <label className={labelClass}>Description</label>
                  <textarea
                    className={`${inputBase} ${inputValid} resize-none`}
                    value={formData.description}
                    onChange={(e) => updateField('description', e.target.value)}
                    rows={2}
                    placeholder="Optional description shown to billing staff."
                  />
                </div>

                <div className="sm:col-span-2 lg:col-span-3 flex flex-wrap items-center gap-x-8 gap-y-3 rounded-lg border border-border bg-background px-4 py-3">
                  <Switch
                    label="Active"
                    title="Available for selection in billing and admission screens."
                    checked={formData.isActive}
                    onChange={(value) => updateField('isActive', value)}
                  />
                  <Switch
                    label="Requires Doctor"
                    title="A doctor must be assigned when this service is billed."
                    checked={formData.requiresDoctor}
                    onChange={(value) => updateField('requiresDoctor', value)}
                  />
                </div>
              </div>

              {/* Pricing & revenue sharing */}
              <div className="border-t border-border">
                <SectionHeader icon={Wallet} title="Pricing & Revenue Sharing" />
              </div>
              <div className="p-5 flex flex-wrap gap-3">
                <div className="flex-1 min-w-[110px]">
                  <label className={labelClass}>Billing Unit</label>
                  <select
                    className={`${inputBase} ${inputValid}`}
                    value={formData.billingUnit}
                    onChange={(e) => updateField('billingUnit', e.target.value)}
                  >
                    {BILLING_UNITS.map((unit) => (
                      <option key={unit.value} value={unit.value}>
                        {unit.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex-1 min-w-[110px]">
                  <label className={labelClass}>
                    {isQuantityBilled ? `Rate (৳)${rateSuffix} *` : 'Price (৳) *'}
                  </label>
                  <NumberField
                    step="0.01"
                    className={ic('price')}
                    value={formData.price}
                    onChange={(value) => updateField('price', value)}
                  />
                  {err('price')}
                </div>

                <div className="flex-1 min-w-[110px]">
                  <label className={labelClass}>Tax %</label>
                  <NumberField
                    step="0.01"
                    className={`${inputBase} ${inputValid}`}
                    value={formData.taxPercent}
                    onChange={(value) => updateField('taxPercent', value)}
                  />
                </div>

                {isQuantityBilled && (
                  <div className="flex-1 min-w-[110px]">
                    <label className={labelClass}>Unit Label</label>
                    <input
                      type="text"
                      className={`${inputBase} ${inputValid}`}
                      value={formData.unitLabel}
                      onChange={(e) => updateField('unitLabel', e.target.value)}
                      placeholder={defaultUnitLabel(formData.billingUnit) || 'hour'}
                      title="Shown on the invoice (e.g. hour, day, session, litre)."
                    />
                  </div>
                )}

                {isQuantityBilled && (
                  <div className="flex-1 min-w-[110px]">
                    <label className={labelClass}>Minimum Units</label>
                    <NumberField
                      step="0.01"
                      min={0}
                      className={`${inputBase} ${inputValid}`}
                      value={formData.minimumUnits}
                      onChange={(value) => updateField('minimumUnits', value)}
                      title="Minimum billed even for a shorter duration (0 = none)."
                    />
                  </div>
                )}

                {formData.billingUnit === 'per_hour' && (
                  <div className="flex-1 min-w-[110px]">
                    <label className={labelClass}>Round Up (min)</label>
                    <NumberField
                      step="1"
                      min={0}
                      className={`${inputBase} ${inputValid}`}
                      value={formData.unitIncrement}
                      onChange={(value) => updateField('unitIncrement', value)}
                      title="Round billable time up to this many minutes (0 = exact). E.g. 15 → 1h05m bills as 1.25h."
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between gap-3 border-t border-border px-5 py-3.5 shrink-0">
              <p className="text-xs text-text-muted hidden sm:block">Fields marked * are required.</p>
              <div className="flex items-center gap-2 ml-auto">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-text-muted hover:text-text transition-colors text-sm flex items-center gap-2"
                >
                  <X size={16} />
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isPending}
                  className="px-5 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium disabled:opacity-50 flex items-center gap-2"
                >
                  {isPending && <Loader2 size={16} className="animate-spin" />}
                  {isEdit ? <Save size={16} /> : <Plus size={16} />}
                  {isEdit ? 'Save Changes' : 'Add Service'}
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

/** Compact card section header. */
function SectionHeader({ icon: Icon, title }: { icon: LucideIcon; title: string }) {
  return (
    <div className="flex items-center gap-2 border-b border-border bg-background/60 px-5 py-2.5">
      <Icon size={15} className="text-primary" />
      <h2 className="text-xs font-semibold uppercase tracking-wide text-text-muted">{title}</h2>
    </div>
  );
}

/** Compact on/off switch used for boolean service flags. */
function Switch({
  label,
  title,
  checked,
  onChange,
}: {
  label: string;
  title?: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      title={title}
      onClick={() => onChange(!checked)}
      className="inline-flex items-center gap-2.5 focus:outline-none focus:ring-2 focus:ring-primary/40 rounded-full"
    >
      <span
        className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${
          checked ? 'bg-primary' : 'bg-border'
        }`}
      >
        <span
          className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
            checked ? 'translate-x-[18px]' : 'translate-x-1'
          }`}
        />
      </span>
      <span className="text-sm font-medium text-text">{label}</span>
    </button>
  );
}

/**
 * Numeric input with friendlier defaults: focusing a field that currently
 * shows `0` clears it so a value can be typed straight away, leaving the field
 * empty restores `0` on blur, and negative values are never accepted.
 */
function NumberField({
  value,
  onChange,
  className,
  ...rest
}: {
  value: number;
  onChange: (value: number) => void;
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type'>) {
  const [text, setText] = useState(String(value ?? 0));
  const [focused, setFocused] = useState(false);

  // Reflect external changes (e.g. loading an existing service) while idle.
  useEffect(() => {
    if (!focused) setText(String(value ?? 0));
  }, [value, focused]);

  const handleFocus = () => {
    setFocused(true);
    if (text !== '' && Number(text) === 0) setText('');
  };

  const handleBlur = () => {
    setFocused(false);
    const parsed = text === '' ? 0 : Number(text);
    const next = Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
    setText(String(next));
    onChange(next);
  };

  const handleChange = (raw: string) => {
    // Strip any minus sign so negative values can never be entered.
    const sanitized = raw.replace(/-/g, '');
    setText(sanitized);
    const parsed = sanitized === '' ? 0 : Number(sanitized);
    onChange(Number.isFinite(parsed) ? Math.max(0, parsed) : 0);
  };

  return (
    <input
      type="number"
      inputMode="decimal"
      min={0}
      className={className}
      value={text}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onChange={(e) => handleChange(e.target.value)}
      {...rest}
    />
  );
}
